import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Search, UserPlus, LogOut, Users, Clock, Settings, KeyRound, Activity, TrendingUp, TrendingDown, Minus, MapPin, ChevronDown, ChevronRight, Radio, Nfc, Ticket as TicketIcon, X, Loader2, CreditCard, Gift, Play } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VenueSwitcher } from "@/components/VenueSwitcher";
import { StaminaBadge } from "@/components/StaminaBadge";
import { KeyboardEmulationAdapter } from "@/services/nfc";
import { toast } from "sonner";

function fmtDuration(iso) {
  const diff = Math.max(Date.now() - new Date(iso).getTime(), 0);
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

function fmtLastVisit(iso) {
  if (!iso) return "Mai";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Oggi";
  if (days === 1) return "Ieri";
  if (days < 30) return `${days}g fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function PlusHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState([]);
  const [searching, setSearching] = useState(false);
  const [, setTick] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [sortBy, setSortBy] = useState("cv");
  const [loadingList, setLoadingList] = useState(true);
  const [zones, setZones] = useState([]);
  const [showList, setShowList] = useState(false);
  const [scanned, setScanned] = useState(null);
  const [scanningTicket, setScanningTicket] = useState(false);
  const fileRef = useRef(null);

  const loadActive = useCallback(() => {
    api.get("/plus/sessions/active").then((r) => setActive(r.data)).catch(() => {});
  }, []);

  const loadZones = useCallback(() => {
    api.get("/plus/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const loadCustomers = useCallback((sort) => {
    setLoadingList(true);
    api.get(`/plus/customers?sort=${sort}&limit=100`)
      .then((r) => setCustomers(r.data))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => { loadActive(); loadZones(); }, [loadActive, loadZones]);
  useEffect(() => { loadCustomers(sortBy); }, [loadCustomers, sortBy]);
  useEffect(() => {
    const i = setInterval(() => { setTick((t) => t + 1); loadActive(); }, 30000);
    return () => clearInterval(i);
  }, [loadActive]);
  useEffect(() => {
    const onVenue = () => { loadActive(); loadZones(); loadCustomers(sortBy); };
    window.addEventListener("venue-changed", onVenue);
    return () => window.removeEventListener("venue-changed", onVenue);
  }, [loadActive, loadZones, loadCustomers, sortBy]);

  const lastUidRef = useRef({ uid: null, t: 0 });
  const handleCard = useCallback(async (uid) => {
    const now = Date.now();
    if (lastUidRef.current.uid === uid && now - lastUidRef.current.t < 1500) return;
    lastUidRef.current = { uid, t: now };
    try {
      const { data } = await api.get(`/plus/cards/lookup/${uid}`);
      if (data.found) {
        setScanned({ customer: data.customer, balance: data.balance, active_session: data.active_session, uid });
        toast.success(`Tessera riconosciuta · ${data.customer.nome} ${data.customer.cognome}`);
      } else {
        setScanned({ customer: null, uid });
        toast.info("Tessera non associata a nessun cliente");
      }
    } catch {
      toast.error("Errore lettura tessera");
    }
  }, []);

  useEffect(() => {
    const adapter = new KeyboardEmulationAdapter();
    adapter.onCardDetected((uid) => handleCard(uid));
    adapter.connect();
    return () => adapter.disconnect();
  }, [handleCard]);

  const scanTicket = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !scanned?.customer) return;
    setScanningTicket(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("customer_id", scanned.customer.id);
      const { data: res } = await api.post("/plus/ticket-analytics/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = res.extracted || {};
      toast.success(`Ticket scansionato · Puntato ${ex.importo ?? "–"}€ · Stamina aggiornata (nessuna Stella)`);
      loadActive();
      loadCustomers(sortBy);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "DUPLICATE_TICKET") toast.error("Ticket già registrato (anti-frode)");
      else toast.error(apiError(err));
    } finally {
      setScanningTicket(false);
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      // UID lookup solo per stringhe che sembrano un UID tessera (esadecimale)
      const uid = q.replace(/[\s:-]/g, "").toUpperCase();
      if (uid.length >= 6 && /^[0-9A-F]+$/.test(uid)) {
        try {
          const { data } = await api.get(`/plus/cards/lookup/${uid}`);
          if (data.found) { navigate(`/customers/${data.customer.id}`); return; }
        } catch {}
      }
      const { data } = await api.get(`/plus/customers/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data);
      if (data.length === 0) toast.info("Nessun cliente trovato");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSearching(false);
    }
  };

  const zoneCounts = {};
  const zoneUsers = {};
  const noZoneUsers = [];
  active.forEach((s) => {
    if (s.zone_current) {
      zoneCounts[s.zone_current] = (zoneCounts[s.zone_current] || 0) + 1;
      (zoneUsers[s.zone_current] = zoneUsers[s.zone_current] || []).push(s);
    } else {
      noZoneUsers.push(s);
    }
  });
  const classTally = (list) => {
    const t = {};
    (list || []).forEach((s) => {
      const lv = s.customer?.cv_level || "N/D";
      t[lv] = (t[lv] || 0) + 1;
    });
    return t;
  };
  const levelColor = {};
  active.forEach((s) => {
    if (s.customer?.cv_level) levelColor[s.customer.cv_level] = s.customer.cv_color;
  });
  const MAP_W = 520, MAP_H = 360;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <button data-testid="plus-home-logo" onClick={() => navigate("/")} className="flex items-center gap-2">
          <Star className="w-7 h-7 text-sky-400 fill-sky-400" />
          <span className="font-display font-bold text-xl">PUNTO STELLA <span className="text-sky-400">PLUS</span></span>
        </button>
        <div className="flex items-center gap-2">
          <VenueSwitcher />
          {user?.ruolo === "ADMIN" && (
            <button data-testid="plus-admin-btn" onClick={() => navigate("/admin")}
              className="flex items-center gap-2 px-4 h-10 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-sky-400/50 text-sm transition-colors">
              <Settings className="w-4 h-4" /> Admin
            </button>
          )}
          <button data-testid="plus-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="flex items-center gap-2 px-4 h-10 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-red-400/50 text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-4 mb-6 items-stretch">
        <div className="lg:flex-1 flex flex-col gap-3">
        <form onSubmit={doSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="plus-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, cellulare o UID tessera…"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-sky-400 transition-colors"
            />
          </div>
          <button data-testid="plus-search-btn" type="submit" disabled={searching}
            className="h-11 px-5 rounded-xl bg-sky-500 hover:bg-sky-400 text-sm font-semibold transition-colors disabled:opacity-60">
            Cerca
          </button>
        </form>

        <div className="flex gap-3">
          <button data-testid="plus-new-customer-btn" onClick={() => navigate("/customers/new")}
            className="flex items-center gap-2 px-5 h-11 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-sky-400/50 text-sm transition-colors">
            <UserPlus className="w-4 h-4 text-sky-400" /> Nuovo cliente
          </button>
          <button data-testid="plus-catalog-btn" onClick={() => navigate("/catalog")}
            className="flex items-center gap-2 px-5 h-11 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-amber-400/50 text-sm transition-colors">
            <Gift className="w-4 h-4 text-amber-400" /> Catalogo
          </button>
          <button data-testid="plus-demo-btn" onClick={() => navigate("/demo")}
            className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 text-white font-semibold text-sm transition-colors shadow-lg shadow-sky-500/20">
            <Play className="w-4 h-4 fill-current" /> DEMO
          </button>
          <div className="flex items-center gap-2 px-4 h-11 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-400 text-xs">
            <KeyRound className="w-4 h-4" /> UID tessera nella ricerca
          </div>
        </div>
        </div>

        <div className="lg:flex-1">
        {(() => {
          const scust = scanned?.customer;
          const listMatch = scust ? customers.find((x) => x.id === scust.id) : null;
          const present = !!scanned?.active_session;
          return (
            <div data-testid="plus-nfc-card"
              className={`h-full rounded-2xl border p-5 transition-colors ${scust ? "bg-green-500/5 border-green-500/40" : scanned ? "bg-amber-500/5 border-amber-500/40" : "bg-zinc-900 border-zinc-800"}`}>
              {!scanned ? (
                <div className="flex items-center gap-5 h-full">
                  <div className="relative flex items-center justify-center w-20 h-20 shrink-0" data-testid="plus-nfc-waiting">
                    <span className="absolute inset-0 rounded-full border-[3px] border-dashed border-red-500 animate-pulse" />
                    <Nfc className="w-8 h-8 text-red-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                      </span>
                      <span className="text-[11px] font-bold tracking-widest text-red-300 uppercase">In attesa tessera</span>
                    </div>
                    <h2 className="font-display font-bold text-xl mt-1">Avvicina la tessera al lettore</h2>
                    <p className="text-zinc-500 text-sm">Il sistema è sempre in ascolto del lettore RFID/NFC.</p>
                  </div>
                </div>
              ) : scust ? (
                <div className="flex flex-col gap-3 h-full justify-center">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-500/15 border-2 border-green-500/50 shrink-0">
                      <CreditCard className="w-6 h-6 text-green-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <button data-testid="plus-nfc-open-customer" onClick={() => navigate(`/customers/${scust.id}`)}
                          className="font-display font-bold text-xl hover:text-sky-300 transition-colors truncate">
                          {scust.nome} {scust.cognome}
                        </button>
                        {listMatch?.cv_level && <StaminaBadge level={listMatch.cv_level} color={listMatch.cv_color} size={18} showLabel={false} />}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap">
                        <span className="inline-flex items-center gap-1 text-sky-400 font-semibold">
                          <Star className="w-3.5 h-3.5 fill-current" /> {scanned.balance} punti
                        </span>
                        {present
                          ? <span className="inline-flex items-center gap-1 text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> in sala</span>
                          : <span className="text-zinc-500">non in sala</span>}
                        <span className="text-zinc-600">UID {scanned.uid}</span>
                      </div>
                    </div>
                    <button data-testid="plus-nfc-reset" onClick={() => setScanned(null)}
                      className="flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-500 transition-colors shrink-0" title="Attendi nuova tessera">
                      <X className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                  <button data-testid="plus-nfc-scan-ticket" onClick={() => fileRef.current?.click()} disabled={scanningTicket}
                    className="flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-400 font-semibold transition-colors disabled:opacity-60">
                    {scanningTicket ? <Loader2 className="w-5 h-5 animate-spin" /> : <TicketIcon className="w-5 h-5" />}
                    Scansiona Ticket VLT
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={scanTicket} className="hidden" data-testid="plus-nfc-file" />
                </div>
              ) : (
                <div className="flex flex-col gap-3 h-full justify-center">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/15 border-2 border-amber-500/50 shrink-0">
                      <CreditCard className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-display font-bold text-lg">Tessera non associata</h2>
                      <p className="text-zinc-500 text-xs truncate">UID {scanned.uid} — nessun cliente collegato.</p>
                    </div>
                    <button data-testid="plus-nfc-reset" onClick={() => setScanned(null)}
                      className="flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-500 transition-colors shrink-0">
                      <X className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                  <button data-testid="plus-nfc-associate" onClick={() => navigate(`/customers/new?uid=${scanned.uid}`)}
                    className="flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-colors">
                    <UserPlus className="w-5 h-5" /> Associa a nuovo cliente
                  </button>
                </div>
              )}
            </div>
          );
        })()}
        </div>
        </div>

        {results.length > 0 && (
          <div data-testid="plus-search-results" className="space-y-2 mb-10">
            {results.map((c) => (
              <button key={c.id} data-testid={`plus-result-${c.id}`} onClick={() => navigate(`/customers/${c.id}`)}
                className="w-full flex items-center justify-between p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-sky-400/50 transition-colors text-left">
                <div>
                  <div className="font-semibold text-lg">{c.nome} {c.cognome}</div>
                  <div className="text-sm text-zinc-500">{c.cellulare}</div>
                </div>
                <div className="flex items-center gap-1 text-amber-400 font-bold">
                  <Star className="w-4 h-4 fill-current" /> {c.balance}
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6 w-full lg:flex-[3]" data-testid="plus-floorplan-card">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-sky-400" />
            <h2 className="font-display font-bold text-xl">Zone della struttura</h2>
            <span data-testid="plus-live-badge" className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-[11px] font-bold tracking-wider text-red-300">LIVE</span>
            </span>
            <span className="ml-auto flex items-center gap-1.5 text-green-400 font-semibold">
              <Users className="w-4 h-4" />
              <span data-testid="plus-floorplan-present">{active.length}</span>
              <span className="text-xs text-zinc-500 font-normal">in sala</span>
            </span>
          </div>

          {zones.length === 0 ? (
            <p className="text-zinc-500 text-sm">Nessuna zona configurata. Aggiungile dal pannello Admin → "Zone della struttura".</p>
          ) : (
            <>
              <div className="w-full overflow-x-auto">
                <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="w-full h-auto rounded-2xl bg-zinc-950 border border-zinc-700" data-testid="plus-floorplan-map">
                  <defs>
                    <pattern id="livegrid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M20 0 L0 0 0 20" fill="none" stroke="#1f2430" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect x="0" y="0" width={MAP_W} height={MAP_H} fill="url(#livegrid)" />
                  {zones.map((z) => {
                    const zu = zoneUsers[z.code] || [];
                    const n = zu.length;
                    return (
                      <g key={z.id} data-testid={`plus-floorplan-zone-${z.code}`}>
                        <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="10"
                          fill={`${z.colore}${n > 0 ? "33" : "18"}`} stroke={z.colore}
                          strokeWidth={n > 0 ? "2.5" : "1.5"} />
                        <text x={z.x + 12} y={z.y + 24} fill={z.colore} fontSize="13" fontWeight="700">{z.nome}</text>
                        {zu.slice(0, 5).map((s, i) => (
                          <g key={s.id}>
                            <circle cx={z.x + 17} cy={z.y + 42 + i * 17} r="4" fill={s.customer?.cv_color || "#a1a1aa"} />
                            <text x={z.x + 27} y={z.y + 46 + i * 17} fontSize="11" fill="#e4e4e7">
                              {`${s.customer?.nome || ""} ${s.customer?.cognome || ""}`.trim().slice(0, 20)}
                            </text>
                          </g>
                        ))}
                        {n > 5 && (
                          <text x={z.x + 17} y={z.y + 46 + 5 * 17} fontSize="10" fill="#a1a1aa">+{n - 5} altri</text>
                        )}
                        {z.counts_for_presence === false && (
                          <text x={z.x + 12} y={z.y + z.h - 8} fill="#71717a" fontSize="9">esclusa dal calcolo</text>
                        )}
                        {n > 0 && (
                          <>
                            <circle cx={z.x + z.w - 20} cy={z.y + 20} r="13" fill="#22c55e" />
                            <circle cx={z.x + z.w - 20} cy={z.y + 20} r="13" fill="none" stroke="#22c55e" strokeWidth="2">
                              <animate attributeName="r" values="13;19;13" dur="1.6s" repeatCount="indefinite" />
                              <animate attributeName="opacity" values="0.9;0;0.9" dur="1.6s" repeatCount="indefinite" />
                            </circle>
                            <text x={z.x + z.w - 20} y={z.y + 25} fill="#052e16" fontSize="13" fontWeight="800" textAnchor="middle">{n}</text>
                          </>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                {zones.map((z) => {
                  const tally = classTally(zoneUsers[z.code]);
                  const total = zoneCounts[z.code] || 0;
                  return (
                    <div key={z.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`plus-zone-legend-${z.code}`}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: z.colore }} />
                      <span className="text-zinc-300 font-medium">{z.nome}</span>
                      <span className={`tabular-nums font-bold ${total > 0 ? "text-green-400" : "text-zinc-600"}`}>{total}</span>
                      <span className="flex flex-wrap gap-1.5">
                        {Object.entries(tally).map(([lv, cnt]) => (
                          <StaminaBadge key={lv} level={lv} color={levelColor[lv]} count={cnt} size={14} />
                        ))}
                      </span>
                    </div>
                  );
                })}
                {noZoneUsers.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="plus-zone-legend-none">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
                    <span className="text-zinc-300 font-medium">Senza zona</span>
                    <span className="tabular-nums font-bold text-amber-400">{noZoneUsers.length}</span>
                    <span className="flex flex-wrap gap-1.5">
                      {Object.entries(classTally(noZoneUsers)).map(([lv, cnt]) => (
                        <StaminaBadge key={lv} level={lv} color={levelColor[lv]} count={cnt} size={14} />
                      ))}
                    </span>
                  </div>
                )}
              </div>
              {noZoneUsers.length > 0 && (
                <div className="mt-3 text-xs text-zinc-500">
                  <Radio className="inline w-3.5 h-3.5 mr-1 text-amber-400" />
                  {noZoneUsers.length} present{noZoneUsers.length === 1 ? "e" : "i"} in sala senza zona assegnata
                </div>
              )}
            </>
          )}
        </div>

        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6 w-full lg:flex-[2]" data-testid="plus-present-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-green-400" />
            <h2 className="font-display font-bold text-xl">Clienti presenti ora</h2>
            <span data-testid="plus-present-count" className="ml-auto text-2xl font-bold text-green-400">{active.length}</span>
          </div>
          {active.length === 0 ? (
            <p className="text-zinc-500 text-sm">Nessuna visita in corso.</p>
          ) : (
            <div className="space-y-2">
              {active.map((s) => (
                <button key={s.id} data-testid={`plus-active-${s.customer_id}`} onClick={() => navigate(`/customers/${s.customer_id}`)}
                  className="w-full flex items-center justify-between gap-2 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-green-400/50 transition-colors text-left">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                      <span className="font-medium truncate">{s.customer?.nome} {s.customer?.cognome}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-4">
                      {s.zone_current
                        ? <span className="text-[10px] text-zinc-400 px-1.5 py-0.5 rounded-full bg-zinc-700">{s.zone_current}</span>
                        : <span className="text-[10px] text-amber-400/80">senza zona</span>}
                      <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                        <Clock className="w-3 h-3" /> {fmtDuration(s.check_in)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span data-testid={`plus-active-balance-${s.customer_id}`} className="inline-flex items-center gap-1 text-amber-400 font-semibold text-sm">
                      <Star className="w-3.5 h-3.5 fill-current" /> {s.customer?.balance ?? 0}
                    </span>
                    {s.customer?.cv_level && (
                      <StaminaBadge level={s.customer.cv_level} color={s.customer.cv_color} size={18} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        </div>

        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 mt-6">
          <button data-testid="plus-list-toggle" onClick={() => setShowList((v) => !v)}
            className="w-full flex flex-wrap items-center gap-2 p-6 text-left">
            {showList ? <ChevronDown className="w-5 h-5 text-zinc-500" /> : <ChevronRight className="w-5 h-5 text-zinc-500" />}
            <Activity className="w-5 h-5 text-sky-400" />
            <h2 className="font-display font-bold text-xl">Clienti registrati</h2>
            <span data-testid="plus-customers-count" className="text-sm text-zinc-500">({customers.length})</span>
          </button>
          {showList && (
          <div className="px-6 pb-6">
          <div className="flex items-center gap-2 mb-4 justify-end">
            <span className="text-xs text-zinc-500">Ordina per</span>
            <select
              data-testid="plus-customers-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white focus:outline-none focus:border-sky-400"
            >
              <option value="cv">Stamina (alta → bassa)</option>
              <option value="balance">Saldo punti (alto → basso)</option>
              <option value="presence">Presenti prima</option>
              <option value="name">Cognome (A → Z)</option>
            </select>
          </div>

          {loadingList ? (
            <p className="text-zinc-500 text-sm">Caricamento…</p>
          ) : customers.length === 0 ? (
            <p className="text-zinc-500 text-sm">Nessun cliente registrato.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="plus-customers-table">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3 font-medium">Cliente</th>
                    <th className="py-2 px-3 font-medium">In sala</th>
                    <th className="py-2 px-3 font-medium">Ultima visita</th>
                    <th className="py-2 px-3 font-medium text-center">Visite 30gg</th>
                    <th className="py-2 px-3 font-medium text-right">Punti Stella</th>
                    <th className="py-2 pl-3 font-medium text-right">Stamina reputazionale</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`plus-customer-row-${c.id}`}
                      onClick={() => navigate(`/customers/${c.id}`)}
                      className="border-b border-zinc-800/60 hover:bg-zinc-800/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 pr-3">
                        <div className="font-medium">{c.nome} {c.cognome}</div>
                        <div className="text-xs text-zinc-500">{c.cellulare}</div>
                      </td>
                      <td className="py-3 px-3">
                        {c.present ? (
                          <span className="inline-flex items-center gap-1.5 text-green-400">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-xs">{c.check_in ? fmtDuration(c.check_in) : "Sì"}</span>
                            {c.zone_current && <span className="text-[10px] text-zinc-500 px-1.5 py-0.5 rounded-full bg-zinc-700">{c.zone_current}</span>}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-xs text-zinc-400">{fmtLastVisit(c.last_visit)}</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`text-xs tabular-nums ${c.visits_30d > 0 ? "text-zinc-300" : "text-zinc-600"}`}>{c.visits_30d}</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className="inline-flex items-center gap-1 text-sky-400 font-semibold">
                          <Star className="w-3.5 h-3.5 fill-current" /> {c.balance}
                        </span>
                      </td>
                      <td className="py-3 pl-3">
                        {c.cv_level ? (
                          <div className="flex items-center justify-end gap-2">
                            {c.cv_trend === "CRESCENTE" && <TrendingUp className="w-3.5 h-3.5 text-green-400" />}
                            {c.cv_trend === "DECRESCENTE" && <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                            {c.cv_trend === "STABILE" && <Minus className="w-3.5 h-3.5 text-zinc-500" />}
                            <span className="tabular-nums font-semibold">{c.cv_score != null ? Math.round(c.cv_score) : "—"}</span>
                            <StaminaBadge level={c.cv_level} color={c.cv_color} size={16} />
                          </div>
                        ) : (
                          <div className="text-right text-xs text-zinc-600">n/d</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
